import { useState } from "react";
import {
  useCall,
  usePagination,
  useFilters,
  notification,
  useDevice,
  useFetch,
  getPaginatedList,
} from "@epcnetwork/core-ui-kit";

import { deleteSource, getSources } from "api";
import { SourceModel } from "models";
import { filtersOptions, getQueryKeys } from "./sources-list.constants";

interface Output {
  editId: string;
  searchValue: string;
  loading: boolean;
  modalFormIsOpen: boolean;
  isMobileDevice: boolean;
  error: string | undefined;
  pagination: ReturnType<typeof usePagination>;
  list: SourceModel[];
  setSearch: (value: string) => void;
  handleEdit: (id: string) => void;
  handleDeleteClick: (id: string) => void;
  onCloseModal: VoidFunction;
  addSourceHandle: VoidFunction;
  refresh: VoidFunction;
}

const useSourcesListHook = (): Output => {
  const { isMobileDevice } = useDevice();
  const { queryString, setSearch, searchValue } = useFilters({
    searchKey: filtersOptions.searchKey,
    nonClearableKeys: getQueryKeys(isMobileDevice),
  });
  const { payload, loading, refresh, error, actions } = useFetch(
    getSources.setQueryParams(queryString),
    { dependencies: [queryString] },
  );
  const list = getPaginatedList(payload?.data);
  const pagination = usePagination(payload);

  const { onCallSuccess, onCallError, submit } = useCall(deleteSource);

  const [modalFormIsOpen, setModalFormIsOpen] = useState(false);
  const [editId, setEditId] = useState("");

  const setFilteredList = (list: SourceModel[]) => {
    if (payload) {
      const { setData } = actions;
      setData({ ...payload, data: list });
    }
  };

  onCallSuccess(() => {
    refresh();
    notification.success("Source deleted", "Successfully deleted a source");
  });

  onCallError((error) => {
    refresh();
    notification.error("Deleting error", `Source was ${error.message}`);
  });

  const onCloseModal = () => {
    setEditId("");
    setModalFormIsOpen(false);
  };

  const addSourceHandle = () => setModalFormIsOpen(true);

  const handleEdit = (id: string) => {
    setEditId(id);
    setModalFormIsOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    notification.confirm("Deleting a Source?", "Are you sure?", {
      onOk: () => {
        submit({ params: { sourceId: id } });
        setFilteredList(list.filter((el) => el.id !== id));
      },
      icon: "delete",
    });
  };

  return {
    editId,
    searchValue,
    loading,
    modalFormIsOpen,
    error: error?.message,
    pagination,
    list,
    setSearch,
    handleEdit,
    handleDeleteClick,
    onCloseModal,
    addSourceHandle,
    refresh,
    isMobileDevice,
  };
};

export default useSourcesListHook;
